/**
 * postMessage Origin Guard
 *
 * Mitigates the ADRUM cross-domain storage vulnerability (CVE-style):
 * The AppDynamics ADRUM agent registers a window 'message' listener that
 * processes any incoming message whose JSON payload has name 'ADRUM_XD'
 * without reliably checking event.origin. An attacker who can frame this
 * page or open it via window.opener could post a crafted message and
 * influence the agent GUID stored in localStorage.
 *
 * This guard installs a *capturing* listener on the window that runs
 * BEFORE the vendor's listener. If the message is an ADRUM_XD payload
 * from an untrusted origin, the event is stopped so it never reaches
 * the vendor code.
 *
 * Allowed origins: same-origin (window.location.origin) plus any
 * explicit entries passed in via the `allowedOrigins` parameter.
 */

/**
 * Returns the set of trusted origins.
 * Always includes the current page origin.
 *
 * @param {string[]} [extraOrigins] - Additional origins to trust
 *   (e.g. 'https://cdn.appdynamics.com').
 * @returns {Set<string>}
 */
export function buildAllowedOrigins(extraOrigins = []) {
    const origins = new Set([window.location.origin]);
    extraOrigins.forEach((o) => {
        if (typeof o === 'string' && o.length > 0) {
            origins.add(o);
        }
    });
    return origins;
}

/**
 * Checks whether a parsed message is an ADRUM cross-domain payload.
 *
 * @param {*} parsed - The JSON-parsed message data.
 * @returns {boolean}
 */
export function isAdrumXdMessage(parsed) {
    return parsed !== null && typeof parsed === 'object' && parsed.name === 'ADRUM_XD';
}

/**
 * Installs the postMessage origin guard on the window.
 *
 * Must be called BEFORE the ADRUM script loads so the capturing listener
 * is registered first.
 *
 * @param {string[]} [extraOrigins] - Additional trusted origins.
 * @returns {function} cleanup — call this to remove the listener.
 */
export function installPostMessageGuard(extraOrigins = []) {
    const allowed = buildAllowedOrigins(extraOrigins);

    function guard(event) {
        // Only inspect string payloads (ADRUM sends JSON strings)
        if (typeof event.data !== 'string') return;

        let parsed;
        try {
            parsed = JSON.parse(event.data);
        } catch (_e) {
            // Not JSON — not an ADRUM message, let it through
            return;
        }

        if (isAdrumXdMessage(parsed) && !allowed.has(event.origin)) {
            // Block the event so the vendor handler never sees it
            event.stopImmediatePropagation();
        }
    }

    // Use capturing phase so this runs before any bubble-phase listeners
    window.addEventListener('message', guard, true);

    return function cleanup() {
        window.removeEventListener('message', guard, true);
    };
}
